"use server";

import { getAppUrl } from "@/lib/app-url";

const STRAPI_API_URL = process.env.STRAPI_API_URL;

export interface TwoFactorResult {
  success: boolean;
  error?: string;
  email?: string;
  requires2FA?: boolean;
  requiresVerification?: boolean;
  redirectUrl?: string;
}

export interface VerifyResult {
  success: boolean;
  error?: string;
  redirectUrl?: string;
}

/**
 * Send 2FA code - validates credentials and triggers verification code email
 * If 2FA is disabled, Strapi returns redirectUrl with auth code directly
 */
export async function send2FACodeAction(
  email: string,
  password: string
): Promise<TwoFactorResult> {
  try {
    if (!STRAPI_API_URL) {
      return { success: false, error: "Server configuration error" };
    }

    const response = await fetch(`${STRAPI_API_URL}/api/saas-auth/send-2fa-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.toLowerCase(), password, origin: await getAppUrl() }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error?.message || "Invalid email or password" };
    }

    // Email not verified - redirect to verification page
    if (data.requiresVerification) {
      return {
        success: true,
        requiresVerification: true,
        email: data.email,
      };
    }

    // 2FA disabled - Strapi returns redirectUrl with auth code
    if (!data.requires2FA && data.redirectUrl) {
      return {
        success: true,
        requires2FA: false,
        redirectUrl: data.redirectUrl,
      };
    }

    // 2FA required - Strapi sent the code via email
    return {
      success: true,
      requires2FA: true,
      email: data.email,
    };
  } catch (error) {
    console.error("Send 2FA code error:", error);
    return { success: false, error: "An unexpected error occurred. Please try again." };
  }
}

/**
 * Verify 2FA code and complete login
 * Strapi returns redirectUrl with auth code → redirect to CRM
 */
export async function verify2FACodeAction(
  email: string,
  code: string
): Promise<VerifyResult> {
  try {
    if (!STRAPI_API_URL) {
      return { success: false, error: "Server configuration error" };
    }

    const response = await fetch(`${STRAPI_API_URL}/api/saas-auth/verify-2fa-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.toLowerCase(), code, origin: await getAppUrl() }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error?.message || "Invalid verification code" };
    }

    if (!data.redirectUrl) {
      return { success: false, error: "Verification failed" };
    }

    return { success: true, redirectUrl: data.redirectUrl };
  } catch (error) {
    console.error("Verify 2FA code error:", error);
    return { success: false, error: "An unexpected error occurred. Please try again." };
  }
}

/**
 * Resend 2FA code
 */
export async function resend2FACodeAction(
  email: string,
  password: string
): Promise<TwoFactorResult> {
  return send2FACodeAction(email, password);
}
